/**
 * BLRRD highlight-recovery — Phase 3.B-color P6.
 *
 * One-shot, in-place reconstruction of clipped highlight channels in a
 * Float32 RGBA scene-linear buffer. Operates AFTER the ingest stage
 * (so the buffer is already in ACEScg / linear-Rec.709 — both linear,
 * channel-independent in the relevant primaries), but BEFORE the kernel
 * runs. Idempotent at the file-load boundary: not a per-frame slider.
 *
 * Algorithm (clean-room, MIT) is the "median-ratio Color Propagation"
 * variant of the Hirakawa-2009 family of opposed-colour reconstruction
 * methods. RawTherapee's "Inpaint Opposed" + "Color Propagation" use the
 * same underlying observation:
 *
 *   For a pixel where channel C is clipped (C ≥ saturation) but the
 *   other two channels are NOT clipped, neighbouring un-clipped pixels
 *   tell us the local C/G or C/B ratio of the scene. Multiplying the
 *   pixel's un-clipped channel by that local ratio yields a plausible
 *   reconstruction.
 *
 * References:
 *   - Hirakawa & Wolfe, "Color Constancy with Spatially-Varying
 *     Illumination from Single Image", IEEE Trans. Img. Proc. 2009.
 *   - RawPedia "Highlight Reconstruction" (RT user manual, NOT source).
 *
 * License discipline: BLRRD is MIT, RawTherapee is GPLv3 — NO RT source
 * was consulted while writing this file. The math is straight from the
 * Hirakawa paper + the public RawPedia article.
 *
 * Phase 3.B.1 follow-ups:
 *   - Replace median-ratio with full opposed-colour least-squares
 *     regression (R = a·G + b·B + c) for measurably better recovery
 *     on saturated coloured highlights (sunset, neon).
 *   - GPU port via WebGL2 fragment shader once the WebGPU lane lands.
 *   - Auto-recover threshold tuning per camera (Canon clips at ~0.99,
 *     Sony clips harder at ~0.97 due to internal headroom rounding).
 *
 * @module src/pipeline/highlight-recovery
 */

/* Bit flags for the per-pixel clip mask (3-bit packed into 1 byte).
 * Exposed so callers can decode masks without re-reading constants. */
export const CLIP_R = 0x01;
export const CLIP_G = 0x02;
export const CLIP_B = 0x04;
export const CLIP_ALL = CLIP_R | CLIP_G | CLIP_B;

/* Default clip threshold. Sensors normalise to 1.0 at saturation but
 * their analog gain + WB scaling can push the white point a hair below
 * 1.0 when libraw applies cam_mul; 0.98 catches the trough cleanly
 * without flagging legitimate scene-linear highlights. HDR inputs
 * (linear-Rec.709 floats > 1.0) are still flagged correctly because the
 * threshold is absolute, not normalised. */
export const DEFAULT_CLIP_THRESHOLD = 0.98;

/* Default neighbourhood radius (3 → 7×7 window). Balances locality
 * (small enough that the C/G ratio represents the same scene region)
 * against statistical reliability (need enough un-clipped neighbours
 * for a stable median). */
export const DEFAULT_NEIGHBORHOOD_RADIUS = 3;

/* Maximum radius to expand to when the initial neighbourhood has no
 * un-clipped neighbours (e.g. centre of a large blown-out sky). 15 →
 * 31×31 window. Beyond that we give up and leave the pixel at its
 * original clipped magnitude — the patch is too large for local
 * opposed-colour reconstruction to be reliable without a global
 * solver, and the pristine input is the most honest answer when no
 * scene context is available. */
export const DEFAULT_MAX_RADIUS = 15;

/* Auto-apply trigger: fraction of pixels with at least one channel
 * clipped. Tuned so that mostly-clean shots stay untouched but a sky
 * with > 5% blown highlights gets recovered automatically. */
export const AUTO_APPLY_THRESHOLD = 0.05;

/**
 * Build a per-pixel clip mask. Each output byte is a 3-bit flag where
 * bit 0 = R clipped, bit 1 = G clipped, bit 2 = B clipped.
 *
 * @param {Float32Array} rgba          scene-linear RGBA, length = w*h*4
 * @param {number} w
 * @param {number} h
 * @param {number} [threshold]         pixels at or above this value are clipped
 * @returns {Uint8Array}               length = w*h
 */
export function buildClipMask(rgba, w, h, threshold = DEFAULT_CLIP_THRESHOLD) {
    if (!rgba || rgba.length !== w * h * 4) {
        throw new Error('buildClipMask: buffer length mismatch');
    }
    const N = w * h;
    const mask = new Uint8Array(N);
    for (let i = 0, j = 0; i < N; i++, j += 4) {
        let m = 0;
        if (rgba[j]     >= threshold) m |= CLIP_R;
        if (rgba[j + 1] >= threshold) m |= CLIP_G;
        if (rgba[j + 2] >= threshold) m |= CLIP_B;
        mask[i] = m;
    }
    return mask;
}

/**
 * Compute clip statistics over a buffer. Used to:
 *   - decide whether to auto-apply recovery (totalClipped > threshold)
 *   - drive the UI badge showing "X% clipped" on the histogram tab
 *
 * @param {Float32Array} rgba
 * @param {number} w
 * @param {number} h
 * @param {number} [threshold]
 * @returns {{
 *   totalClipped: number,
 *   perChannel: [number, number, number],
 *   allChannelsClipped: number
 * }}   fractions in [0, 1] (multiply by 100 for percent)
 */
export function highlightStats(rgba, w, h, threshold = DEFAULT_CLIP_THRESHOLD) {
    if (!rgba || rgba.length !== w * h * 4) {
        throw new Error('highlightStats: buffer length mismatch');
    }
    const N = w * h;
    let nR = 0, nG = 0, nB = 0, nAny = 0, nAll = 0;
    for (let i = 0; i < N; i++) {
        const j = i * 4;
        const cr = rgba[j]     >= threshold;
        const cg = rgba[j + 1] >= threshold;
        const cb = rgba[j + 2] >= threshold;
        if (cr) nR++;
        if (cg) nG++;
        if (cb) nB++;
        if (cr || cg || cb) nAny++;
        if (cr && cg && cb) nAll++;
    }
    const inv = N > 0 ? 1 / N : 0;
    return {
        totalClipped: nAny * inv,
        perChannel: [nR * inv, nG * inv, nB * inv],
        allChannelsClipped: nAll * inv,
    };
}

/**
 * Median of a small array of finite numbers. Sorts in place — caller
 * must own the array (we always pass a freshly-pushed one). Empty input
 * returns NaN so callers can branch on it.
 */
function medianInPlace(arr) {
    const n = arr.length;
    if (n === 0) return NaN;
    arr.sort((a, b) => a - b);
    const mid = n >> 1;
    return (n & 1) ? arr[mid] : 0.5 * (arr[mid - 1] + arr[mid]);
}

/**
 * Median ratio of channel C to a reference channel R over a
 * neighbourhood, considering ONLY un-clipped pixels (per-channel mask).
 * Returns NaN when no usable samples exist.
 *
 * @param {Float32Array} rgba
 * @param {Uint8Array} mask       per-pixel clip flags
 * @param {number} w
 * @param {number} h
 * @param {number} cx             centre x
 * @param {number} cy             centre y
 * @param {number} radius
 * @param {number} cIdx           clipped channel index (0|1|2)
 * @param {number} rIdx           reference (un-clipped) channel index
 * @param {number} cFlag          clip flag for cIdx (CLIP_R|CLIP_G|CLIP_B)
 * @param {number} rFlag          clip flag for rIdx
 * @param {number[]} scratch      reusable scratch array (.length=0 on entry)
 * @param {number} epsilon        avoid /0 when reference is dark
 * @returns {number}              median ratio (cVal / rVal), or NaN
 */
function medianRatio(rgba, mask, w, h, cx, cy, radius,
                     cIdx, rIdx, cFlag, rFlag, scratch, epsilon) {
    scratch.length = 0;
    const x0 = Math.max(0, cx - radius);
    const y0 = Math.max(0, cy - radius);
    const x1 = Math.min(w - 1, cx + radius);
    const y1 = Math.min(h - 1, cy + radius);
    for (let y = y0; y <= y1; y++) {
        const row = y * w;
        for (let x = x0; x <= x1; x++) {
            const p = row + x;
            const m = mask[p];
            /* Need a pixel where BOTH the clipped channel C and the
             * reference channel R are un-clipped — that's the only
             * sample where we observe an honest C/R ratio. */
            if ((m & cFlag) !== 0) continue;
            if ((m & rFlag) !== 0) continue;
            const j = p * 4;
            const rv = rgba[j + rIdx];
            if (rv < epsilon) continue;
            const cv = rgba[j + cIdx];
            scratch.push(cv / rv);
        }
    }
    return medianInPlace(scratch);
}

/**
 * Recover a single clipped pixel by the median-ratio method.
 *
 * For a pixel at (x, y) where channel C is clipped and at least one
 * other channel is NOT clipped, predict C as:
 *
 *     C ≈ median_q∈N(p)  C_q / R_q   ×  R_p
 *
 * where R is whichever un-clipped channel has the highest signal at p
 * (most reliable reference). When both un-clipped channels are
 * available we average their independent predictions for robustness.
 *
 * If no usable neighbourhood is found at the given radius, we return
 * a sentinel NaN — the caller decides whether to expand the radius
 * or leave the pixel's original clipped value in place.
 */
function recoverPixelChannel(rgba, mask, w, h, x, y, radius,
                             cIdx, cFlag, scratch, epsilon) {
    const p = y * w + x;
    const j = p * 4;
    /* Identify the two non-clipped reference channels (if any). */
    const m = mask[p];
    const refs = [];
    if (cIdx !== 0 && (m & CLIP_R) === 0) refs.push({ idx: 0, flag: CLIP_R });
    if (cIdx !== 1 && (m & CLIP_G) === 0) refs.push({ idx: 1, flag: CLIP_G });
    if (cIdx !== 2 && (m & CLIP_B) === 0) refs.push({ idx: 2, flag: CLIP_B });
    if (refs.length === 0) return NaN;

    let sum = 0;
    let count = 0;
    for (let k = 0; k < refs.length; k++) {
        const ref = refs[k];
        const ratio = medianRatio(rgba, mask, w, h, x, y, radius,
                                  cIdx, ref.idx, cFlag, ref.flag,
                                  scratch, epsilon);
        if (Number.isFinite(ratio)) {
            const refVal = rgba[j + ref.idx];
            if (refVal >= epsilon) {
                sum += ratio * refVal;
                count++;
            }
        }
    }
    if (count === 0) return NaN;
    return sum / count;
}

/**
 * In-place highlight recovery. Mutates and returns `rgba`.
 *
 * Algorithm: median-ratio Color Propagation (Hirakawa-style opposed-colour
 * recovery). For each pixel where one or two channels are clipped but
 * at least one is not, the clipped channel(s) are reconstructed using
 * the local C/R ratio of nearby fully-resolved pixels. Pixels with all
 * three channels clipped get an expanded-radius search that ALSO
 * considers neighbours that were recovered in phase A (so a fully
 * blown core can inherit colour from its partially-recovered ring).
 * If nothing is found within `maxRadius`, the original clipped value
 * is left in place — the pristine 1.0 (or sensor-saturation magnitude)
 * is the most honest answer when no scene context is available.
 *
 * Pixels whose channels are ALL below `threshold` are touched zero
 * times — guaranteed by the early-out on `mask[p] === 0`.
 *
 * @param {Float32Array} rgba                 scene-linear RGBA, length=w*h*4
 * @param {number} w
 * @param {number} h
 * @param {object} [opts]
 * @param {number} [opts.threshold=0.98]
 * @param {number} [opts.neighborhoodRadius=3]
 * @param {number} [opts.maxRadius=15]        cap when expanding the search
 * @param {number} [opts.epsilon=1e-4]        floor for reference-channel
 *                                            magnitude (avoid /0 in dark px)
 * @param {string} [opts.mode='opposed']      'opposed' (default) | 'desaturate'
 *                                            'desaturate' clips the channel
 *                                            to the per-pixel max of the
 *                                            un-clipped channels — cheaper,
 *                                            duller, useful as a fallback.
 * @returns {Float32Array}                    same buffer reference
 */
export function recoverHighlights(rgba, w, h, opts) {
    if (!rgba || rgba.length !== w * h * 4) {
        throw new Error('recoverHighlights: buffer length mismatch');
    }
    const o = opts || {};
    const threshold = (typeof o.threshold === 'number')
        ? o.threshold : DEFAULT_CLIP_THRESHOLD;
    const baseRadius = Math.max(1, (o.neighborhoodRadius ?? DEFAULT_NEIGHBORHOOD_RADIUS) | 0);
    const maxRadius  = Math.max(baseRadius, (o.maxRadius ?? DEFAULT_MAX_RADIUS) | 0);
    const epsilon    = (typeof o.epsilon === 'number' && o.epsilon > 0)
        ? o.epsilon : 1e-4;
    const mode = o.mode || 'opposed';

    const mask = buildClipMask(rgba, w, h, threshold);
    const scratch = [];

    /* First pass — collect all clipped pixel indices. We need a stable
     * mask of "what was clipped" because the recovery loop mutates
     * `rgba`. If we read the mask from the live buffer mid-loop, an
     * early recovery would un-flag downstream pixels and skew their
     * ratio statistics. */
    const clippedIdx = [];
    for (let i = 0; i < mask.length; i++) {
        if (mask[i] !== 0) clippedIdx.push(i);
    }
    if (clippedIdx.length === 0) return rgba;

    /* Two-phase recovery:
     *   Phase A: pixels with at least one un-clipped channel — median
     *            ratio against neighbours.
     *   Phase B: pixels with ALL channels clipped — expand the search
     *            radius and inherit colour from neighbours that were
     *            EITHER originally un-clipped OR successfully recovered
     *            by phase A. The `recovered` companion mask flags the
     *            second case so phase B can see those pixels as usable
     *            references; without this, an all-clipped core
     *            surrounded only by partial-clip-and-recovered pixels
     *            would stay white (Codex P6 r1 STOP — phase-B
     *            eligibility gap).
     *
     * We DON'T re-flag the original `mask` bits during phase A — that
     * would change ratio statistics for downstream phase-A pixels. We
     * track recovery success in a separate Uint8Array. */
    const recovered = new Uint8Array(mask.length);
    const allClipped = [];

    for (let k = 0; k < clippedIdx.length; k++) {
        const p = clippedIdx[k];
        const m = mask[p];
        if (m === CLIP_ALL) {
            allClipped.push(p);
            continue;
        }
        const x = p % w;
        const y = (p - x) / w;
        const j = p * 4;

        if (mode === 'desaturate') {
            /* Cheap fallback: bring clipped channel down to per-pixel
             * max of the un-clipped channels. Avoids hue rotation but
             * loses brightness information. */
            const r = rgba[j], g = rgba[j + 1], b = rgba[j + 2];
            const refMax = Math.max(
                (m & CLIP_R) ? -Infinity : r,
                (m & CLIP_G) ? -Infinity : g,
                (m & CLIP_B) ? -Infinity : b,
            );
            if (Number.isFinite(refMax)) {
                if (m & CLIP_R) rgba[j]     = refMax;
                if (m & CLIP_G) rgba[j + 1] = refMax;
                if (m & CLIP_B) rgba[j + 2] = refMax;
            }
            continue;
        }

        /* Opposed-colour median-ratio reconstruction. */
        let radius = baseRadius;
        let recoveredR = NaN, recoveredG = NaN, recoveredB = NaN;
        while (radius <= maxRadius) {
            if (m & CLIP_R) {
                if (!Number.isFinite(recoveredR)) {
                    recoveredR = recoverPixelChannel(rgba, mask, w, h, x, y,
                        radius, 0, CLIP_R, scratch, epsilon);
                }
            }
            if (m & CLIP_G) {
                if (!Number.isFinite(recoveredG)) {
                    recoveredG = recoverPixelChannel(rgba, mask, w, h, x, y,
                        radius, 1, CLIP_G, scratch, epsilon);
                }
            }
            if (m & CLIP_B) {
                if (!Number.isFinite(recoveredB)) {
                    recoveredB = recoverPixelChannel(rgba, mask, w, h, x, y,
                        radius, 2, CLIP_B, scratch, epsilon);
                }
            }
            const doneR = !(m & CLIP_R) || Number.isFinite(recoveredR);
            const doneG = !(m & CLIP_G) || Number.isFinite(recoveredG);
            const doneB = !(m & CLIP_B) || Number.isFinite(recoveredB);
            if (doneR && doneG && doneB) break;
            radius = Math.min(radius * 2, maxRadius + 1);
            if (radius > maxRadius) break;
        }

        /* Apply recovered values where we got them; otherwise leave
         * the original clipped value alone (will be re-clamped by the
         * downstream view-transform anyway). The recovered value can
         * legitimately exceed 1.0 — that's HDR headroom recovered
         * from the un-clipped channels. */
        let allFilled = true;
        if (m & CLIP_R) {
            if (Number.isFinite(recoveredR)) rgba[j]     = recoveredR;
            else allFilled = false;
        }
        if (m & CLIP_G) {
            if (Number.isFinite(recoveredG)) rgba[j + 1] = recoveredG;
            else allFilled = false;
        }
        if (m & CLIP_B) {
            if (Number.isFinite(recoveredB)) rgba[j + 2] = recoveredB;
            else allFilled = false;
        }
        /* Mark this pixel as a usable phase-B reference IFF every
         * clipped channel was successfully filled. A partial-recovery
         * pixel could still leak the original clipped magnitude on the
         * unfilled channel(s), so it's safer to exclude it from
         * phase-B reference candidates. */
        if (allFilled) recovered[p] = 1;
    }

    /* Phase B — fully-clipped pixels. Expand the radius progressively
     * looking for any usable neighbour. A neighbour is usable iff:
     *   (a) it was originally un-clipped (mask[q] === 0), OR
     *   (b) it was successfully recovered in phase A (recovered[q] === 1).
     * If we find one, use the neighbour's average colour; otherwise
     * leave the pixel at its original clipped magnitude — the pristine
     * input is the most honest answer when no scene context is found. */
    for (let k = 0; k < allClipped.length; k++) {
        const p = allClipped[k];
        const x = p % w;
        const y = (p - x) / w;
        const j = p * 4;

        let r = NaN, g = NaN, b = NaN;
        let radius = baseRadius;
        while (radius <= maxRadius && (!Number.isFinite(r) || !Number.isFinite(g) || !Number.isFinite(b))) {
            const x0 = Math.max(0, x - radius);
            const y0 = Math.max(0, y - radius);
            const x1 = Math.min(w - 1, x + radius);
            const y1 = Math.min(h - 1, y + radius);
            let sumR = 0, sumG = 0, sumB = 0, count = 0;
            for (let yy = y0; yy <= y1; yy++) {
                const row = yy * w;
                for (let xx = x0; xx <= x1; xx++) {
                    const q = row + xx;
                    /* Usable iff originally un-clipped OR phase-A
                     * recovered. Skip phase-B-target pixels (the
                     * remaining all-clipped patch) and partially-
                     * recovered pixels that may still hold the original
                     * clipped magnitude on the unfilled channel. */
                    if (mask[q] !== 0 && recovered[q] !== 1) continue;
                    const jq = q * 4;
                    sumR += rgba[jq];
                    sumG += rgba[jq + 1];
                    sumB += rgba[jq + 2];
                    count++;
                }
            }
            if (count > 0) {
                r = sumR / count;
                g = sumG / count;
                b = sumB / count;
                break;
            }
            radius = Math.min(radius * 2, maxRadius + 1);
            if (radius > maxRadius) break;
        }
        if (Number.isFinite(r)) rgba[j]     = r;
        if (Number.isFinite(g)) rgba[j + 1] = g;
        if (Number.isFinite(b)) rgba[j + 2] = b;
        /* All-clipped pixels with no usable neighbours retain their
         * original clipped value — typically 1.0 for sensor saturation. */
    }

    return rgba;
}
